import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, KeyRound, CheckCircle2, ExternalLink, Eye, EyeOff } from "lucide-react";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null); // { email_delivery_enabled, dev_reset_link? }

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const r = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(r.data);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="forgot-password-page">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-12">
        <Card className="p-6 sm:p-8 border-slate-200">
          <div className="w-11 h-11 rounded-xl bg-blue-50 grid place-items-center text-blue-700 mb-3">
            <KeyRound className="w-6 h-6" />
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Forgot your password?</h1>
          <p className="text-sm text-slate-500 mt-1">
            Enter the email you registered with — we&apos;ll send a link to reset your password. Patients sign in using mobile OTP instead.
          </p>

          {!sent ? (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-blue-700 hover:bg-blue-800"
                data-testid="forgot-submit-btn"
              >
                {loading ? "Sending…" : "Send reset link"}
              </Button>
              <div className="text-xs text-slate-500 text-center">
                Remembered it? <Link to="/login" className="text-blue-700 font-semibold" data-testid="back-to-login-link">Back to sign in</Link>
              </div>
            </form>
          ) : (
            <div className="mt-6 space-y-4" data-testid="forgot-success">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                <div className="text-sm text-emerald-900 leading-relaxed">
                  If an account exists for <b>{email}</b>, a reset link has been sent.
                  It will expire in <b>30 minutes</b> for your security.
                </div>
              </div>

              {!sent.email_delivery_enabled && sent.dev_reset_link && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="forgot-dev-fallback">
                  <div className="text-xs font-semibold uppercase tracking-wider text-amber-800 flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5" /> Development mode
                  </div>
                  <p className="text-xs text-amber-900 mt-1 leading-relaxed">
                    Email sending is not yet configured. Copy the reset link below and open it in your browser, or share it with the user directly.
                  </p>
                  <a
                    href={sent.dev_reset_link}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-900 underline break-all"
                    data-testid="forgot-dev-link"
                  >
                    <ExternalLink className="w-3.5 h-3.5 shrink-0" /> {sent.dev_reset_link}
                  </a>
                </div>
              )}

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setSent(null); }}
                  className="text-slate-600 hover:text-slate-900"
                  data-testid="forgot-try-again-btn"
                >
                  Try a different email
                </button>
                <Link to="/login" className="text-blue-700 font-semibold">Back to sign in</Link>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  // Simple strength hint
  const strong =
    pwd.length >= 12 && /[A-Z]/.test(pwd) && /[a-z]/.test(pwd) && /\d/.test(pwd) && /[^A-Za-z0-9]/.test(pwd);
  const medium = !strong && pwd.length >= 8;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) { setErr("Missing or invalid reset link."); return; }
    if (pwd.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pwd !== confirm) { setErr("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => nav("/login"), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not reset password. Request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="reset-password-page">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-12">
        <Card className="p-6 sm:p-8 border-slate-200">
          <div className="w-11 h-11 rounded-xl bg-blue-50 grid place-items-center text-blue-700 mb-3">
            <KeyRound className="w-6 h-6" />
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Set a new password</h1>
          <p className="text-sm text-slate-500 mt-1">
            Choose a strong password — at least <b>8 characters</b>. 12+ with mixed case, numbers and symbols is ideal.
          </p>

          {!token && (
            <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900" data-testid="reset-missing-token">
              This reset link is missing the token. Please request a new one from the{" "}
              <Link to="/forgot-password" className="font-semibold underline">forgot password page</Link>.
            </div>
          )}

          {token && !done && (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label>New password</Label>
                <div className="relative">
                  <Input
                    type={show ? "text" : "password"}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    data-testid="reset-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShow(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    aria-label={show ? "Hide password" : "Show password"}
                    data-testid="reset-toggle-visibility"
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {pwd.length > 0 && (
                  <div className="mt-1.5 flex items-center gap-2 text-[11px]" data-testid="reset-strength-meter">
                    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full transition-all ${strong ? "bg-emerald-500 w-full" : medium ? "bg-amber-500 w-2/3" : "bg-red-500 w-1/3"}`} />
                    </div>
                    <span className={strong ? "text-emerald-700" : medium ? "text-amber-700" : "text-red-700"}>
                      {strong ? "Strong" : medium ? "Fair" : "Weak"}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <Label>Confirm password</Label>
                <Input
                  type={show ? "text" : "password"}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-password-input"
                />
              </div>
              {err && <div className="text-sm text-red-600" data-testid="reset-error">{err}</div>}
              <Button
                type="submit"
                disabled={loading || !pwd || !confirm}
                className="w-full h-11 bg-blue-700 hover:bg-blue-800"
                data-testid="reset-submit-btn"
              >
                {loading ? "Saving…" : "Update password"}
              </Button>
              <div className="text-xs text-slate-500 text-center">
                <Link to="/login" className="text-blue-700 font-semibold">Back to sign in</Link>
              </div>
            </form>
          )}

          {done && (
            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3" data-testid="reset-success">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
              <div className="text-sm text-emerald-900 leading-relaxed">
                Your password has been updated. Redirecting you to sign in…
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
